import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Image } from "react-bootstrap";
import Swal from "sweetalert2";
import { post, put } from "../API/api_helper";

interface Props {
  show: boolean;
  onHide: () => void;
  company?: any | null;
  test: string;
}
const CompanyModal: React.FC<Props> = ({ show, onHide, company, test }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_code: "+91",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    street: "",
    pincode: "",
    state: "",
    country: "",
    website: "",
    logo: null as File | null,
  });

  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>("");

  // ✅ Phone validation logic
  const validatePhone = (code: string, phone: string): string => {
    const cleaned = phone.replace(/\D/g, ""); // remove non-digits
    if (code === "+91" && cleaned.length !== 10)
      return "Indian phone numbers must be 10 digits.";
    if (code === "+27" && cleaned.length !== 9)
      return "South African phone numbers must be 9 digits.";
    if (code === "+266" && cleaned.length !== 8)
      return "Lesotho phone numbers must be 8 digits.";
    return "";
  };

  // ✅ Handle changes for all inputs
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "phone" || name === "phone_code") {
      const code = name === "phone_code" ? value : formData.phone_code;
      const phone = name === "phone" ? value : formData.phone;
      setPhoneError(validatePhone(code, phone));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, logo: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone_code: "+91",
      phone: "",
      address1: "",
      address2: "",
      city: "",
      street: "",
      pincode: "",
      state: "",
      country: "",
      website: "",
      logo: null,
    });
    setPreview(null);
    setPhoneError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ Final phone validation before submit
    const validationMessage = validatePhone(formData.phone_code, formData.phone);
    if (validationMessage) {
      setPhoneError(validationMessage);
      Swal.fire({
        icon: "error",
        title: "Invalid Phone Number",
        text: validationMessage,
      });
      return;
    }

    try {
      setLoading(true);

      let logoBase64: string | null = null;
      if (formData.logo instanceof File) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(formData.logo as Blob);
        });
        logoBase64 = base64;
      }

      const payload = {
        ...formData,
        logo: logoBase64,
      };

      let response;
      if (company?.id) {
        response = await put(`/organizations/${company.id}`, payload);
      } else {
        response = await post("/organizations", payload);
      }

      Swal.fire({
        icon: "success",
        title: "Organizations details saved successfully!",
        timer: 1500,
        showConfirmButton: false,
      });

      resetForm();
      onHide();
    } catch (error: any) {
      console.error("Backend error:", error.response?.data);
      const errorMsg =
        error.response?.data?.message || "Please check all fields and try again.";

      const validationErrors = error.response?.data?.errors;
      let formattedErrors = "";
      if (validationErrors) {
        formattedErrors = Object.values(validationErrors).flat().join("\n");
      }

      Swal.fire({
        icon: "error",
        title: "Failed to save",
        text: formattedErrors || errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company) {
      setFormData({
        ...formData,
        ...company,
        logo: null,
      });
      setPreview(company.logo ? company.logo : null);
    }
  }, [company]);

  // useEffect(() => {
  //   if (show) {
  //     console.log("test", test);
  //   }
  // }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Enter Company Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="logo" className="mb-3">
            <Form.Label>Company Logo</Form.Label>
            <Form.Control type="file" name="logo" accept="image/*" onChange={handleFileChange} />
            {preview && (
              <div className="mt-2 text-center">
                <Image src={preview} alt="Preview" thumbnail style={{ maxHeight: "120px" }} />
              </div>
            )}
          </Form.Group>

          <Form.Group controlId="name" className="mb-3">
            <Form.Label>Company Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Enter company name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} />
            </Col>

            <Col md={4}>
              <Form.Label>Phone Code</Form.Label>
              <Form.Select name="phone_code" value={formData.phone_code} onChange={handleChange}>
                <option value="+91">+91 (India)</option>
                <option value="+27">+27 (South Africa)</option>
                <option value="+266">+266 (Lesotho)</option>
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter valid phone number"
                required
                isInvalid={!!phoneError}
              />
              {phoneError && (
                <Form.Control.Feedback type="invalid">{phoneError}</Form.Control.Feedback>
              )}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Label>Address 1</Form.Label>
              <Form.Control name="address1" value={formData.address1} onChange={handleChange} />
            </Col>
            <Col md={6}>
              <Form.Label>Address 2</Form.Label>
              <Form.Control name="address2" value={formData.address2} onChange={handleChange} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={4}>
              <Form.Label>City</Form.Label>
              <Form.Control name="city" value={formData.city} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Form.Label>Street / Area</Form.Label>
              <Form.Control name="street" value={formData.street} onChange={handleChange} />
            </Col>
            <Col md={4}>
              <Form.Label>Pincode</Form.Label>
              <Form.Control name="pincode" value={formData.pincode} onChange={handleChange} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <Form.Label>State</Form.Label>
              <Form.Control name="state" value={formData.state} onChange={handleChange} />
            </Col>
            <Col md={6}>
              <Form.Label>Country</Form.Label>
              <Form.Control name="country" value={formData.country} onChange={handleChange} />
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Label>Website (optional)</Form.Label>
            <Form.Control
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://example.com"
            />
          </Form.Group>

          <div className="text-end mt-4">
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CompanyModal;
